import io from 'socket.io-client';
import { store } from '../store';
import {
  setConnected,
  setRoomId,
  addActiveUser,
  removeActiveUser,
  addLock,
  removeLock,
  updateCursor,
  removeCursor,
  setTyping,
  updateActivity,
} from '../store/slices/rfpCollaborationSlice';
import { updateField } from '../store/slices/rfpTemplateSlice';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

class SocketService {
  constructor() {
    this.socket = null;
    this.currentTemplateId = null;
  }

  connect(user) {
    if (this.socket?.connected) {
      return this.socket;
    }

    this.socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    // Connection events
    this.socket.on('connect', () => {
      console.log('Socket.IO connected:', this.socket.id);
      store.dispatch(setConnected(true));
    });

    this.socket.on('disconnect', () => {
      console.log('Socket.IO disconnected');
      store.dispatch(setConnected(false));
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
    });

    // Collaboration events
    this.setupCollaborationHandlers();

    return this.socket;
  }

  setupCollaborationHandlers() {
    if (!this.socket) return;

    // User joined
    this.socket.on('user_joined', (data) => {
      console.log('User joined:', data.user_name);
      store.dispatch(addActiveUser({
        id: data.user_id,
        name: data.user_name,
        sid: data.sid,
      }));
    });

    // User left
    this.socket.on('user_left', (data) => {
      console.log('User left:', data.user_name);
      store.dispatch(removeActiveUser(data.user_id));
      store.dispatch(removeCursor(data.user_id));
    });

    // Field updated
    this.socket.on('field_updated', (data) => {
      console.log('Field updated:', data.field_path, 'by', data.user_name);

      // Only update if from another user
      const currentUser = store.getState().rfpCollaboration.currentUser;
      if (data.user_id !== currentUser?.id) {
        store.dispatch(updateField({
          path: data.field_path,
          value: data.value,
        }));
      }

      store.dispatch(updateActivity());
    });

    // Lock acquired
    this.socket.on('lock_acquired', (data) => {
      console.log('Lock acquired:', data.field_path, 'by', data.user_name);
      store.dispatch(addLock({
        field_path: data.field_path,
        user_id: data.user_id,
        user_name: data.user_name,
        locked_at: data.locked_at,
      }));
    });

    // Lock released
    this.socket.on('lock_released', (data) => {
      console.log('Lock released:', data.field_path);
      store.dispatch(removeLock(data.field_path));
    });

    // Cursor moved
    this.socket.on('cursor_moved', (data) => {
      store.dispatch(updateCursor({
        user_id: data.user_id,
        field_path: data.field_path,
        position: data.position,
      }));
    });

    // Typing indicator
    this.socket.on('user_typing', (data) => {
      store.dispatch(setTyping({
        user_id: data.user_id,
        field_path: data.field_path,
        is_typing: data.is_typing,
      }));
    });
  }

  // Join template room
  joinTemplate(templateId, user) {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }

    this.currentTemplateId = templateId;

    this.socket.emit('join_template', {
      template_id: templateId,
      user_id: user.id,
      user_name: user.name,
    });

    store.dispatch(setRoomId(templateId));
    console.log('Joined template room:', templateId);
  }

  // Leave template room
  leaveTemplate(templateId) {
    if (!this.socket?.connected || !this.currentTemplateId) {
      return;
    }

    this.socket.emit('leave_template', {
      template_id: templateId || this.currentTemplateId,
    });

    this.currentTemplateId = null;
    store.dispatch(setRoomId(null));
    console.log('Left template room');
  }

  // Send field update
  updateField(templateId, fieldPath, value, user) {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }

    this.socket.emit('field_update', {
      template_id: templateId,
      field_path: fieldPath,
      value,
      user_id: user.id,
      user_name: user.name,
    });
  }

  // Acquire lock
  acquireLock(templateId, fieldPath, user) {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }

    this.socket.emit('acquire_lock', {
      template_id: templateId,
      field_path: fieldPath,
      user_id: user.id,
      user_name: user.name,
    });
  }

  // Release lock
  releaseLock(templateId, fieldPath) {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }

    this.socket.emit('release_lock', {
      template_id: templateId,
      field_path: fieldPath,
    });
  }

  // Send cursor position
  moveCursor(templateId, fieldPath, position, user) {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit('cursor_move', {
      template_id: templateId,
      field_path: fieldPath,
      position,
      user_id: user.id,
      user_name: user.name,
    });
  }

  // Send typing indicator
  setTyping(templateId, fieldPath, isTyping, user) {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit('typing', {
      template_id: templateId,
      field_path: fieldPath,
      is_typing: isTyping,
      user_id: user.id,
      user_name: user.name,
    });
  }

  // Disconnect
  disconnect() {
    if (this.currentTemplateId) {
      this.leaveTemplate(this.currentTemplateId);
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

// Singleton instance
const socketService = new SocketService();

export default socketService;
